// 회사소개 페이지 데이터. 출처: 회사소개자료(국문) 1~2장, HMM 제안서 회사 소개
import type { Lang } from "@/lib/useLang";

export const ABOUT: Record<
  Lang,
  {
    eyebrow: string;
    title: string;
    desc: string;
    seoTitle: string;
    seoDescription: string;
    values: { icon: string; title: string; desc: string }[];
    valuesSummary: string;
    overviewTitle: string;
    overview: { label: string; value: string }[];
    businessTitle: string;
    business: { icon: string; title: string; desc: string }[];
    orgTitle: string;
    orgDesc: string;
    org: { name: string; items: string[] }[];
    staffTitle: string;
    staffDesc: string;
    staff: { title: string; desc: string }[];
    tiers: { grade: string; role: string }[];
    historyTitle: string;
    history: { year: string; items: string[] }[];
  }
> = {
  ko: {
    eyebrow: "ABOUT OCEAN CONNECT",
    title: "고객의 안전 운항을 책임지는 선박 IT 파트너",
    desc: "주식회사 오션커넥트는 해양 환경에 특화된 IT 서비스로 선박의 안전성과 운항 효율성을 극대화하여 고객의 성공적인 해상 비즈니스를 지원합니다.",
    seoTitle: "회사소개 — 부산 선박 IT · 해양 IT 서비스 전문기업",
    seoDescription: "2019년 설립, 부산 소재 선박 IT 통합관리 전문기업 오션커넥트. ISO 45001 · 벤처기업 · 기업부설연구소. 선박 IT 유지보수, 네트워크 구축, 위성통신 커미셔닝, 사이버보안, 원격관리를 한 팀이 수행합니다.",
    values: [
      { icon: "ri-user-heart-line", title: "고객 중심", desc: "선사와 선원의 현장 요구사항을 깊이 이해하고, 맞춤형 솔루션을 신속하게 제공하여 운영 효율성을 극대화합니다." },
      { icon: "ri-medal-line", title: "전문성", desc: "해양 IT 분야에서 축적한 전문성으로 선박의 핵심 자산인 IT 시스템과 네트워크 인프라를 최적화합니다." },
      { icon: "ri-hand-heart-line", title: "신뢰", desc: "투명한 서비스와 체계적인 품질 관리로 고객과 장기적인 신뢰 관계를 구축하고, 안전한 해양 네트워크 환경을 함께 만듭니다." },
    ],
    valuesSummary: "선박이 어느 바다에 있든, IT가 멈추지 않도록 설계부터 운영까지 책임지는 것, 그것이 오션커넥트의 일입니다.",
    overviewTitle: "회사 개요",
    overview: [
      { label: "회사명", value: "주식회사 오션커넥트 (OCEAN CONNECT Co., Ltd.)" },
      { label: "설립일", value: "2019년 6월 11일" },
      { label: "사업 분야", value: "조선해양 IT 서비스 제공 및 솔루션 개발" },
      { label: "본사", value: "부산광역시 남구 신선로 365, 용당9관 218호" },
      { label: "인증 현황", value: "ISO 45001 안전보건경영시스템 · 벤처기업 인증" },
      { label: "기업부설연구소", value: "2023년 설립 · 선박 IT 전용 솔루션 개발 · 해상환경 특화 기술 연구" },
      { label: "등록 업종", value: "선박수리업 · 선용품공급업" },
      { label: "서비스 지역", value: "국내 주요 항만 방선 (부산 · 울산 · 광양 · 여수 · 포항 · 인천 · 평택 등), 해외 기항지 원격 지원 · 출장" },
    ],
    businessTitle: "주요 사업",
    business: [
      { icon: "ri-tools-line", title: "선박 IT 통합 유지보수", desc: "24/7 상시 모니터링 체계로 선박 내 모든 IT 인프라에 대한 신속한 대응 서비스를 제공합니다." },
      { icon: "ri-router-line", title: "네트워크 인프라 구축 · 컨설팅", desc: "해양 환경에 최적화된 고신뢰성 유·무선 네트워크 설계, 구축 및 전문 컨설팅을 제공합니다." },
      { icon: "ri-shield-keyhole-line", title: "사이버 보안 솔루션", desc: "해양 특화 방화벽, 엔드포인트 보안, 접근제어 솔루션으로 선박 네트워크와 데이터 보안을 강화합니다." },
      { icon: "ri-remote-control-line", title: "원격 관리 · 모니터링", desc: "육상에서 선박의 IT 시스템을 실시간 진단하고 선제적으로 관리하는 지능형 RMM 서비스를 제공합니다." },
      { icon: "ri-radar-line", title: "위성통신 · CCTV 시스템", desc: "고대역폭 VSAT 솔루션과 고해상도 CCTV 시스템 공급 및 전문 엔지니어링 서비스를 제공합니다." },
      { icon: "ri-ship-line", title: "조선해양 특수 기자재", desc: "CITADEL 비상통신, 디지털 경사계 등 안전 운항을 위한 첨단 장비와 산업용 IT 하드웨어를 공급합니다." },
    ],
    orgTitle: "조직 구성",
    orgDesc: "분야별 전문 조직이 유기적으로 협업해 고객 선단에 책임감 있는 밀착 지원을 제공합니다.",
    org: [
      { name: "기술지원팀", items: ["선박 네트워크 설계 · 구축 · 컨설팅", "선박 주요 시스템 설치 · 커미셔닝", "24/7 통합 유지보수 · 원격 지원 · 장애 처리"] },
      { name: "연구개발팀", items: ["선박 IT 전용 솔루션 개발", "해상환경 특화 기술 연구", "신규 기술 도입 및 기술 검증"] },
      { name: "영업팀", items: ["IT 솔루션 · 시스템 제안, 기술 영업", "국내외 파트너사 협력 및 영업망 관리"] },
      { name: "경영관리팀", items: ["회계 · 자금 · 구매 · 공급망 관리", "인사 · 총무 · 사내 복지"] },
    ],
    staffTitle: "전문 기술 인력",
    staffDesc: "접수부터 근본원인 해결까지 4단계 격상 체계로 지원합니다. 분야별 전문 엔지니어가 고객의 문제를 신속하고 깊이 있게 해결하며, 장애 난이도에 따라 단계적으로 격상하여 근본 원인까지 책임집니다.",
    staff: [
      { title: "선박 IT 솔루션 개발", desc: "해양 환경에 최적화된 맞춤형 소프트웨어 및 시스템 개발" },
      { title: "선박 네트워크 설계", desc: "선박의 특수 환경을 고려한 안정적 네트워크 구성 설계" },
      { title: "선박 IT 유지보수", desc: "24/7 원격 및 현장 기술 지원 서비스 제공" },
      { title: "설치 · 커미셔닝", desc: "전문 엔지니어의 장비 설치 및 최적화 서비스" },
    ],
    tiers: [
      { grade: "L1", role: "접수 · 1차 대응 — 헬프데스크 단일 창구로 장애 · 견적 접수" },
      { grade: "L2", role: "원격 조치 · 국내외 방선 정비" },
      { grade: "L3", role: "고난도 · 복합 원인 정밀 분석, 커미셔닝 총괄" },
      { grade: "PM", role: "근본원인 분석 · 솔루션 개발 · 재발 방지" },
    ],
    historyTitle: "연혁",
    history: [
      { year: "2026", items: ["하나로해운 사선 전선대 통합 IT 관리계약 최초 수주", "PAN OCEAN 사선 전선대 노후 보안장비 공급 및 설치", "PAN OCEAN 사선 전선대, STX OCEAN SERVICE 관리선 통합 IT 관리계약 유지 중"] },
      { year: "2023", items: ["벤처기업 인증 · 기업부설연구소 설립", "STX OCEANSERVICE 관리선 통합 IT 관리계약 최초 수주", "PAN OCEAN 사선 전선대 중앙관리형 엔드포인트 공급 및 설치"] },
      { year: "2022", items: ["KDDI 관리선박 VSAT 위성통신 시스템 커미셔닝 수행", "KDDI 계약선박 MAIL 솔루션 변경 지원"] },
      { year: "2021", items: ["글로벌 선사 대상 선박 IoT 시스템 및 신조선 네트워크 구축"] },
      { year: "2020", items: ["선박 기상관측장비 설치 사업 수행 (기상청)", "PAN OCEAN 사선 전선대 WiFi Management Solutions 공급 및 구축", "PAN OCEAN 사선 전선대 E-Mail Sharing Solutions 공급 및 설치"] },
      { year: "2019", items: ["주식회사 오션커넥트 법인 설립 (6월 11일)", "선박수리업 · 선용품공급업 등록", "PAN OCEAN 사선 통합 IT 관리계약 최초 수주 및 보안장비 일괄 구축"] },
    ],
  },
  en: {
    eyebrow: "ABOUT OCEAN CONNECT",
    title: "The vessel IT partner responsible for your safe navigation",
    desc: "Ocean Connect Co., Ltd. maximizes vessel safety and operational efficiency with IT services specialized for the marine environment, supporting our clients' success at sea.",
    seoTitle: "About Us — Ship IT Support & Maritime IT Services, Busan Korea",
    seoDescription: "Founded 2019 in Busan, Ocean Connect is a vessel IT integrated management specialist (ISO 45001, certified venture, corporate R&D center): vessel IT maintenance, onboard networks, satellite commissioning, cyber security, and remote management by one team.",
    values: [
      { icon: "ri-user-heart-line", title: "Customer-centric", desc: "We understand the on-site needs of shipping companies and crews, and deliver tailored solutions quickly to maximize operational efficiency." },
      { icon: "ri-medal-line", title: "Expertise", desc: "With deep maritime IT expertise, we optimize the IT systems and network infrastructure that are a vessel's core assets." },
      { icon: "ri-hand-heart-line", title: "Trust", desc: "Transparent service and systematic quality management build long-term trust and a safer maritime network environment." },
    ],
    valuesSummary: "Wherever a vessel sails, we take responsibility from design to operation so its IT never stops — that is what Ocean Connect does.",
    overviewTitle: "Company Overview",
    overview: [
      { label: "Company", value: "OCEAN CONNECT Co., Ltd." },
      { label: "Founded", value: "June 11, 2019" },
      { label: "Business", value: "Maritime IT services and solution development" },
      { label: "Head Office", value: "Room 218, Yongdang Building 9, 365 Sinseon-ro, Nam-gu, Busan, Korea" },
      { label: "Certifications", value: "ISO 45001 · Certified Venture Company" },
      { label: "R&D Center", value: "Corporate R&D center since 2023 · vessel-specific IT solutions · marine-environment technology research" },
      { label: "Registrations", value: "Ship repair · Ship supply" },
      { label: "Service Area", value: "Onboard service at major Korean ports (Busan, Ulsan, Gwangyang, Yeosu, Pohang, Incheon, Pyeongtaek); overseas ports by remote support and travel" },
    ],
    businessTitle: "Main Business",
    business: [
      { icon: "ri-tools-line", title: "Integrated Vessel IT Maintenance", desc: "Rapid response for all onboard IT infrastructure through a 24/7 monitoring system." },
      { icon: "ri-router-line", title: "Network Infrastructure & Consulting", desc: "Design, deployment, and consulting for highly reliable wired/wireless networks optimized for the marine environment." },
      { icon: "ri-shield-keyhole-line", title: "Cyber Security Solutions", desc: "Marine-specific firewalls, endpoint security, and access control to protect vessel networks and data." },
      { icon: "ri-remote-control-line", title: "Remote Management & Monitoring", desc: "Intelligent RMM service that diagnoses and proactively manages vessel IT systems from shore in real time." },
      { icon: "ri-radar-line", title: "Satellite & CCTV Systems", desc: "High-bandwidth VSAT solutions and high-resolution CCTV systems with professional engineering services." },
      { icon: "ri-ship-line", title: "Marine Specialty Equipment", desc: "Advanced equipment for safe navigation such as CITADEL emergency communication and digital inclinometers, plus industrial IT hardware." },
    ],
    orgTitle: "Organization",
    orgDesc: "Specialist teams work closely together to give every fleet responsive, accountable support.",
    org: [
      { name: "Technical Support", items: ["Vessel network design, deployment, consulting", "Installation and commissioning of key systems", "24/7 integrated maintenance, remote support, fault handling"] },
      { name: "R&D", items: ["Vessel-specific IT solution development", "Marine-environment technology research", "New technology adoption and verification"] },
      { name: "Sales", items: ["IT solution and system proposals, technical sales", "Domestic and overseas partner management"] },
      { name: "Management", items: ["Accounting, finance, procurement, supply chain", "HR, general affairs, employee benefits"] },
    ],
    staffTitle: "Technical Staff",
    staffDesc: "A four-level escalation path from intake to root-cause resolution. Specialist engineers resolve issues quickly and thoroughly, escalating by difficulty until the root cause is fixed.",
    staff: [
      { title: "Vessel IT Solution Development", desc: "Custom software and systems optimized for the marine environment" },
      { title: "Vessel Network Design", desc: "Stable network architecture for the special conditions on board" },
      { title: "Vessel IT Maintenance", desc: "24/7 remote and on-site technical support" },
      { title: "Installation & Commissioning", desc: "Expert installation and optimization of equipment" },
    ],
    tiers: [
      { grade: "L1", role: "Intake & first response — single helpdesk for faults and quotes" },
      { grade: "L2", role: "Remote fix & onboard service in Korea and overseas" },
      { grade: "L3", role: "Complex fault analysis & commissioning lead" },
      { grade: "PM", role: "Root cause, solution & recurrence prevention" },
    ],
    historyTitle: "History",
    history: [
      { year: "2026", items: ["First integrated IT management contract for the entire HANARO SHIPPING owned fleet", "Replacement security equipment supplied and installed across the entire PAN OCEAN owned fleet", "Integrated IT management contracts ongoing for the entire PAN OCEAN owned fleet and STX OCEAN SERVICE managed vessels"] },
      { year: "2023", items: ["Certified venture company · corporate R&D center established", "First integrated IT management contract for STX OCEANSERVICE managed vessels", "Centrally managed endpoint security supplied and installed across the entire PAN OCEAN owned fleet"] },
      { year: "2022", items: ["VSAT commissioning for KDDI managed vessels", "Mail solution migration support for KDDI contracted vessels"] },
      { year: "2021", items: ["Vessel IoT system and newbuild network deployment for global shipping companies"] },
      { year: "2020", items: ["Vessel weather observation equipment installation (Korea Meteorological Administration)", "WiFi Management Solutions supplied and deployed across the entire PAN OCEAN owned fleet", "E-Mail Sharing Solutions supplied and installed across the entire PAN OCEAN owned fleet"] },
      { year: "2019", items: ["Ocean Connect Co., Ltd. founded (June 11)", "Registered for ship repair and ship supply", "First integrated IT management contract for PAN OCEAN owned vessels and fleet-wide security equipment deployment"] },
    ],
  },
};
